import { EPaguelaSituation } from '@financial/data-access';
import { EShortcutIdentifier } from '@shared/components/shortcuts/data-access';

export type TResponsible = {
  PersonId: string;
  Kinship: EKinshipType | null;
  FiscalIdentity: string | null;
  PhoneNumber: string | null;
  FullPhoneNumber: string | null;
  CountryCode: number | null;
  FirstName: string | null;
  LastName: string | null;
  TenantId: string | null;
  Email: string | null;
  Operation: number | null;
  ValidationResult: string | null;
  IsValid: boolean;
};

export enum EPersonType {
  Person = 1,
  Company = 2,
}

export enum EGenderType {
  Others = 0,
  Male = 1,
  Female = 2,
}

export enum ERegistrationType {
  Contact,
  Customer,
  Helpdesk,
  Supplier,
  User,
  Visitor,
}

export const registrationShortcutIdentifier = {
  [ERegistrationType.Customer]: EShortcutIdentifier.NewClient,
  [ERegistrationType.Visitor]: EShortcutIdentifier.NewVisitor,
};

export const registrationTitles = {
  [ERegistrationType.Contact]: {
    title: 'PROFILE.EDIT.REGISTRATION.CONTACT.TITLE',
    subtitle: 'PROFILE.EDIT.REGISTRATION.CONTACT.SUBTITLE',
  },
  [ERegistrationType.Customer]: {
    title: 'PROFILE.EDIT.REGISTRATION.CUSTOMER.TITLE',
    subtitle: 'PROFILE.EDIT.REGISTRATION.CUSTOMER.SUBTITLE',
  },
  [ERegistrationType.Visitor]: {
    title: 'PROFILE.EDIT.REGISTRATION.VISITOR.TITLE',
    subtitle: 'PROFILE.EDIT.REGISTRATION.VISITOR.SUBTITLE',
  },
  [ERegistrationType.User]: {
    title: 'PROFILE.EDIT.REGISTRATION.USER.TITLE',
    subtitle: 'PROFILE.EDIT.REGISTRATION.USER.SUBTITLE',
  },
};

export const registrationTitlesSwitch = {
  [ERegistrationType.Contact]: {
    title: 'PROFILE.EDIT.ACTIVATE_CONTACT',
    subtitle: 'PROFILE.EDIT.ACTIVATE_CONTACT_DESCRIPTION',
  },
  [ERegistrationType.Customer]: {
    title: 'PROFILE.EDIT.ACTIVATE_CUSTOMER',
    subtitle: 'PROFILE.EDIT.ACTIVATE_CUSTOMER_DESCRIPTION',
  },
  [ERegistrationType.Visitor]: {
    title: 'PROFILE.EDIT.ACTIVATE_VISITOR',
    subtitle: 'PROFILE.EDIT.ACTIVATE_VISITOR_DESCRIPTION',
  },
  [ERegistrationType.User]: {
    title: 'PROFILE.EDIT.ACTIVATE_USER',
    subtitle: 'PROFILE.EDIT.ACTIVATE_USER_DESCRIPTION',
  },
};

export enum EPhoneType {
  Home = 1,
  Mobile = 2,
  Commerce = 3,
  Other = 4,
  MobileNotification = 5,
}

export enum EAddressType {
  Others = 0,
  Home = 1,
  Business = 2,
}

export type TProfile = {
  PersonId: string;
  IsLegacy: boolean | null;
  TenantId: string | null;
  AbstractedTenantId: string | null;
  FriendlyCode: string | null;
  CallCode: string | null;
  IsOwner: boolean | null;
  IsActive: boolean | null;
  FirstName: string | null;
  LastName: string | null;
  FullName: string | null;
  DisplayName: string | null;
  FriendlyName: string | null;
  LegalBusinessName: string | null;
  TradeName: string | null;
  Email: string | null;
  Registration?: string | null;
  PersonType: EPersonType | null;
  GenderType: EGenderType | null;
  IdentityDocument: string | null;
  CompanyIdentityDocument: string | null;
  IdentityDocumentComplement: string | null;
  FiscalIdentity: string | null;
  CompanyFiscalIdentity: string | null;
  Birthdate: string | null;
  Age: number | null;
  Tags: string | null;
  RegistrationType: ERegistrationType | null;
  AbstractedRegistrationType: ERegistrationType | null;
  ActivitiesIds?: string[] | null;
  GroupId: string | null;
  ModuleId: string | null;
  AddedOn: string | null;
  AddedBy: string | null;
  UpdatedOn: string | null;
  UpdatedBy: string | null;
  DeletedOn: string | null;
  DeletedBy: string | null;
  Phones: TPhone[];
  Addresses: TAddress[];
  UserConsolidated: TUserConsolidated;
  OperationOrigemIsLegacy: boolean | null;
  CallerData: TCallerData;
  Operation: string | null;
  Preference?: TPreference;
  Responsible?: TResponsible;
  HistoryConsolidated?: THistoryConsolidated;
  EventConsolidated?: TEventConsolidated;
  Integration?: TIntegration;
  Tax: TTax;
  Access?: TAccess;
  ValidationResult: string | null;
  IsValid: boolean;
  FullDisplayName: string;
};

export type TSimpleLoginCode = {
  AddedBy?: string;
  AddedOn?: string;
  ExpiresIn?: string;
  LoginCodeId?: string;
  PersonId?: string;
  Sid?: string;
  TenantId?: string;
  Type?: string;
};

export type TIntegration = {
  PagueLaId?: string;
};

export type TTax = {
  PersonId: string;
  TenantId: string;
  Type?: ETaxRegimeType;
  EspecialType?: ESpecialTaxRegimeType;
  StartOn?: string;
  EndOn?: string;
};

export type TEventConsolidated = {
  MaxAccessAllowedOn: string | null;
};

export type THistoryConsolidated = {
  AccessBlockedOn: string | null;
  LastNameChangedOn: string | null;
  LastLoggedOn: string | null;
};

export type TPreference = {
  ConsultantPersonId?: string;
  PersonId?: string;
  TenantId?: string;
  PreferredDueDay?: string;
};

export type TPhone = {
  PhoneId: string;
  PersonId: string | null;
  TenantId: string | null;
  Type: EPhoneType | null;
  Number: string | null;
  CountryCode: number | null;
  Confirmed: boolean | null;
  Comment: string | null;
  FullNumber: string | null;
};

export type TAddress = {
  AddressId: string;
  PersonId: string | null;
  TenantId: string | null;
  Type: EAddressType | null;
  CountryCode: string | null;
  StreetType: string | null;
  Street: string | null;
  Number: string | null;
  Complement: string | null;
  RegionName: string | null;
  City: string | null;
  District: string | null;
  ZipCode: string | null;
  CityCode: string | null;
  IsFiscal: boolean | null;
};

export type TUserConsolidated = {
  PersonId: string;
  RoleId: string | null;
  RoleDescription: string | null;
  PasswordCreatedOn: string | null;
  PasswordResetedOn: string | null;
  LastLoginOn: string | null;
  LastLoginApp: string | null;
  Operation: string | null;
  ValidationResult: string | null;
  IsValid: boolean;
};

export type TCallerData = {
  MemberName: string | null;
  SourceFilePath: string | null;
  SourceLineNumber: number | null;
};

export type TComment = {
  CommentId?: string;
  Title: string;
  Read?: boolean;
  Content: string;
  AddedBy?: string;
  AddedOn?: string;
  UpdatedOn?: string;
  UserName?: string;
  TargetPersonId?: string;
  SourcePersonId?: string;
};

export type TCommentToggleRead = {
  CommentId: string;
  Read: boolean;
};

export type TActivity = {
  ActivityId?: string;
  Description: string;
  Icon?: string;
};

export type TAccess = {
  AccessPin?: string | null;
  AccessMessage?: string | null;
};

export const predefinedActivities: TActivity[] = [
  {
    Description: 'Beach Tennis',
    Icon: 'racquet',
  },
  {
    Description: 'Boxe',
    Icon: 'boxing-glove',
  },
  {
    Description: 'Taekwondo',
    Icon: 'boxing-glove',
  },
  {
    Description: 'Muay Thai',
    Icon: 'boxing-glove',
  },
  {
    Description: 'Basquete',
    Icon: 'basketball',
  },
  {
    Description: 'Capoeira',
    Icon: 'scarf',
  },
  {
    Description: 'Cross Fit',
    Icon: 'dumbbell-ray',
  },
  {
    Description: 'Cross Training',
    Icon: 'dumbbell-ray',
  },
  {
    Description: 'Musculação',
    Icon: 'dumbbell-horizontal',
  },
  {
    Description: 'Corrida',
    Icon: 'running',
  },
  {
    Description: 'Funcional',
    Icon: 'running',
  },
  {
    Description: 'FitDance',
    Icon: 'swing',
  },
  {
    Description: 'Futebol',
    Icon: 'court-sport',
  },
  {
    Description: 'Futevôlei',
    Icon: 'football',
  },
  {
    Description: 'Hidroginástica',
    Icon: 'swimmer',
  },
  {
    Description: 'Natação',
    Icon: 'swimmer',
  },
  {
    Description: 'Jiu Jitsu',
    Icon: 'uniform-martial-arts',
  },
  {
    Description: 'Judô',
    Icon: 'uniform-martial-arts',
  },
  {
    Description: 'Karatê',
    Icon: 'uniform-martial-arts',
  },
  {
    Description: 'Pilates',
    Icon: 'person-pilates',
  },
  {
    Description: 'Ritmos',
    Icon: 'ballet-dance',
  },
  {
    Description: 'Spinning',
    Icon: 'biking',
  },
  {
    Description: 'Tênis',
    Icon: 'tennis',
  },
  {
    Description: 'Vôlei',
    Icon: 'volleyball',
  },
  {
    Description: 'Vôlei de Areia',
    Icon: 'volleyball',
  },
  {
    Description: 'Yoga',
    Icon: 'meditation',
  },
  {
    Description: 'Zumba',
    Icon: 'transporter',
  },
];

export const mockedActivities: TActivity[] = [
  {
    Description: 'Ritmos',
    Icon: 'ballet-dance',
  },
  {
    Description: 'Spinning',
    Icon: 'biking',
  },
  {
    Description: 'Taekwondo',
    Icon: 'boxing-glove',
  },
];

export const menuOptions = ({
  isAdmin,
  isPaguelaAdmin,
  registrationType,
}: {
  isAdmin?: boolean;
  isPaguelaAdmin?: boolean;
  registrationType: ERegistrationType;
}) => {
  const mockMenuOptions: {
    Id: string;
    Description: string;
    Group: string;
    RightIcon?: string;
    onlyTypes?: ERegistrationType[];
    isAdmin?: boolean;
    isPaguelaAdmin?: boolean;
  }[] = [
    {
      Id: 'convert_to_customer',
      Description: 'PROFILE.HEADER_ACTIONS.CONVERT_TO_CUSTOMER',
      Group: 'first',
      onlyTypes: [ERegistrationType.Visitor],
    },
    {
      Id: '1',
      Description: 'PROFILE.HEADER_ACTIONS.EDIT_PROFILE',
      Group: 'first',
    },
    {
      Id: '2',
      Description: 'PROFILE.HEADER_ACTIONS.ADD_SUBSCRIPTION',
      Group: 'second',
      onlyTypes: [ERegistrationType.Customer, ERegistrationType.Visitor],
    },
    {
      Id: '3',
      Description: 'PROFILE.HEADER_ACTIONS.ADD_SERVICE',
      Group: 'second',
    },
    {
      Id: '4',
      Description: 'PROFILE.HEADER_ACTIONS.ADD_SALE',
      Group: 'second',
    },
    {
      Id: '5',
      Description: 'PROFILE.HEADER_ACTIONS.NEW_WORKOUT_SHEET',
      Group: 'third',
    },
    {
      Id: '6',
      Description: 'PROFILE.HEADER_ACTIONS.NEW_PHYSICAL_ASSESSMENT',
      Group: 'third',
    },
    {
      Id: '7',
      Description: 'PROFILE.HEADER_ACTIONS.NEW_GRADUATION',
      Group: 'third',
      onlyTypes: [ERegistrationType.Customer],
    },
    {
      Id: '8',
      Description: 'PROFILE.HEADER_ACTIONS.SEND_CRM',
      Group: 'fourth',
      onlyTypes: [ERegistrationType.Customer, ERegistrationType.Visitor],
    },
    {
      Id: '9',
      Description: 'PROFILE.HEADER_ACTIONS.ADD_CONTRACT',
      Group: 'fourth',
      onlyTypes: [ERegistrationType.Customer],
    },
    {
      Id: '10',
      Description: 'PROFILE.HEADER_ACTIONS.ADD_DOCUMENT',
      Group: 'fourth',
      onlyTypes: [ERegistrationType.Customer],
    },
    {
      Id: '11',
      Description: 'PROFILE.HEADER_ACTIONS.SEND_EMAIL',
      Group: 'fifth',
      onlyTypes: [ERegistrationType.Customer, ERegistrationType.User],
    },
    {
      Id: '12',
      Description: 'PROFILE.HEADER_ACTIONS.REGISTER_BIOMETRICS',
      Group: 'fifth',
      RightIcon: 'text-muted-foreground text-[16px] fi-rr-fingerprint',
    },
    {
      Id: '13',
      Description: 'PROFILE.HEADER_ACTIONS.ADD_COMMENT',
      Group: 'fourth',
    },
    {
      Id: 'new_return',
      Description: 'PROFILE.HEADER_ACTIONS.SEND_RETURN',
      Group: 'fourth',
      onlyTypes: [ERegistrationType.Customer],
      isPaguelaAdmin: true,
    },
  ];

  return mockMenuOptions.filter((tab) => {
    const canShowByTypes =
      (tab.onlyTypes && tab.onlyTypes.includes(registrationType)) ||
      !tab.onlyTypes;
    const canShowByAdmin = (tab.isAdmin && isAdmin) || !tab.isAdmin;
    const canShowByPaguelaAdmin =
      (tab.isPaguelaAdmin && isPaguelaAdmin) || !tab.isPaguelaAdmin;

    return canShowByTypes && canShowByAdmin && canShowByPaguelaAdmin;
  });
};

export const tabs = ({
  isAdmin,
  isPaguelaAdmin,
  registrationType,
  isMobile,
}: {
  isAdmin?: boolean;
  isPaguelaAdmin?: boolean;
  registrationType: ERegistrationType;
  isMobile?: boolean;
}) => {
  const mockTabs: {
    id: string;
    description: string;
    icon?: string;
    hideDescription?: boolean;
    route: string;
    onlyTypes?: ERegistrationType[];
    isAdmin?: boolean;
    isPaguelaAdmin?: boolean;
    isMobile?: boolean;
  }[] = [
    {
      id: 'profile-data',
      description: 'PROFILE.TABS.PROFILE_DATA',
      icon: 'user',
      hideDescription: true,
      route: 'profile',
      isMobile: true
    },
    {
      id: '1',
      description: 'PROFILE.TABS.INSIGHTS',
      icon: 'street-view',
      hideDescription: true,
      route: 'insights',
    },
    {
      id: '2',
      description: 'PROFILE.TABS.TIMELINE',
      icon: 'branching',
      hideDescription: true,
      route: 'timeline',
    },
    { id: '8', description: 'PROFILE.TABS.FINANCIAL', route: 'financial' },
    {
      id: '3',
      description: 'PROFILE.TABS.SUBSCRIPTIONS',
      route: 'subscriptions',
      onlyTypes: [ERegistrationType.Customer],
    },
    { id: '4', description: 'PROFILE.TABS.SERVICES', route: 'services' },
    { id: '5', description: 'PROFILE.TABS.GOODS', route: 'sales' },
    {
      id: '6',
      description: 'TREINO.WORKOUT_SHEETS.PERSONAL_WORKOUT_SHEETS.WORKOUTS',
      route: 'workout-sheets',
    },
    // { id: '7', description: 'PROFILE.TABS.SCHEDULE', onlyTypes: [ERegistrationType.Customer], route: null },
    { id: '9', description: 'PROFILE.TABS.AFIG', route: 'afig' },
    {
      id: '10',
      description: 'PROFILE.TABS.GRADUATIONS',
      route: 'graduations',
      onlyTypes: [ERegistrationType.Customer],
    },
    {
      id: '11',
      description: 'PROFILE.TABS.CONTRACTS',
      route: 'contracts',
      onlyTypes: [ERegistrationType.Customer, ERegistrationType.Visitor],
    },
    { id: '13', description: 'PROFILE.TABS.ACCESS', route: 'accesses' },
    {
      id: '14',
      description: 'PROFILE.TABS.DOCUMENTS',
      route: 'documents',
      onlyTypes: [ERegistrationType.Customer],
    },
    { id: '15', description: 'PROFILE.TABS.COMMENTS', route: 'comments' },
    {
      id: '16',
      description: 'PROFILE.TABS.HELPDESK',
      route: 'helpdesk',
      onlyTypes: [ERegistrationType.Customer],
      isAdmin: true,
    },
    {
      id: '17',
      description: 'PROFILE.TABS.BILLS',
      route: 'bills',
      onlyTypes: [ERegistrationType.Customer],
      isAdmin: true,
    },
    {
      id: '18',
      description: 'PROFILE.TABS.RETURNS',
      route: 'returns',
      onlyTypes: [ERegistrationType.Customer],
      isPaguelaAdmin: true,
    },
    {
      id: '19',
      description: 'PROFILE.TABS.AVERAGE_TICKET',
      route: 'average-ticket',
      onlyTypes: [ERegistrationType.Customer],
    },
    {
      id: '20',
      description: 'PROFILE.TABS.ACQUIRERS',
      route: 'acquirers',
      onlyTypes: [ERegistrationType.Customer],
      isPaguelaAdmin: true,
    },
    {
      id: '21',
      description: 'PROFILE.CREDIT_ASSESSMENT.CREDIT_ASSESSMENT',
      route: 'credit-assessment',
      onlyTypes: [ERegistrationType.Customer, ERegistrationType.Visitor],
      isAdmin: true,
    },
    {
      id: '22',
      description: 'PROFILE.TABS.MEETINGS',
      route: 'meetings',
      onlyTypes: [ERegistrationType.Customer],
      isAdmin: true,
    },
    {
      id: '23',
      description: 'PROFILE.TABS.CRM',
      route: 'crm',
      onlyTypes: [ERegistrationType.Customer, ERegistrationType.Visitor],
    },
  ];

  return mockTabs.filter((tab) => {
    const canShowByTypes =
      (tab.onlyTypes && tab.onlyTypes.includes(registrationType)) ||
      !tab.onlyTypes;
    const canShowByAdmin = (tab.isAdmin && isAdmin) || !tab.isAdmin;
    const canShowByPaguelaAdmin =
      (tab.isPaguelaAdmin && isPaguelaAdmin) || !tab.isPaguelaAdmin;
    const canShowByMobile = (tab.isMobile && isMobile) || !tab.isMobile;


    return canShowByTypes && canShowByAdmin && canShowByPaguelaAdmin && canShowByMobile;
  });
};

export const districts = [
  { Description: 'AC', Value: 'AC' },
  { Description: 'AL', Value: 'AL' },
  { Description: 'AM', Value: 'AM' },
  { Description: 'AP', Value: 'AP' },
  { Description: 'BA', Value: 'BA' },
  { Description: 'CE', Value: 'CE' },
  { Description: 'DF', Value: 'DF' },
  { Description: 'ES', Value: 'ES' },
  { Description: 'GO', Value: 'GO' },
  { Description: 'MA', Value: 'MA' },
  { Description: 'MG', Value: 'MG' },
  { Description: 'MS', Value: 'MS' },
  { Description: 'MT', Value: 'MT' },
  { Description: 'PA', Value: 'PA' },
  { Description: 'PB', Value: 'PB' },
  { Description: 'PE', Value: 'PE' },
  { Description: 'PI', Value: 'PI' },
  { Description: 'PR', Value: 'PR' },
  { Description: 'RR', Value: 'RR' },
  { Description: 'RO', Value: 'RO' },
  { Description: 'RS', Value: 'RS' },
  { Description: 'SC', Value: 'SC' },
  { Description: 'SE', Value: 'SE' },
  { Description: 'SP', Value: 'SP' },
  { Description: 'TO', Value: 'TO' },
  { Description: 'RJ', Value: 'RJ' },
  { Description: 'RN', Value: 'RN' },
];

export const commentActions = [
  {
    id: 'view',
    description: 'PROFILE.COMMENTS.VIEW',
  },
  {
    id: 'edit',
    description: 'PROFILE.COMMENTS.EDIT',
  },
  {
    id: 'read',
    description: 'PROFILE.COMMENTS.MARK_AS_READ',
  },
  {
    id: 'delete',
    description: 'PROFILE.COMMENTS.DELETE',
  },
];

export enum EKinshipType {
  BoyfriendGirlfriend = 0,
  Spouse = 1,
  Sibling = 2,
  Parent = 3,
  UncleAunt = 4,
  Grandparent = 5,
  Guardian = 6,
  Others = 7,
}

/**
 * @description Enum representing the types of tax regimes
 */
export enum ETaxRegimeType {
  Nenhum = 0,
  SimplesNacional = 1,
  SimplesNacionalExcesso = 2,
  NormalLucroPresumido = 3,
  NormalLucroReal = 4,
}

/**
 * @description Enum representing the types of special tax regimes
 */
export enum ESpecialTaxRegimeType {
  SemRegimeTributarioEspecial = 0,
  MicroEmpresaMunicipal = 1,
  Estimativa = 2,
  SociedadeDeProfissionais = 3,
  Cooperativa = 4,
  MicroempresarioIndividualMEI = 5,
  MicroempresaOuPequenoPorteMEEPP = 6,
  LucroReal = 7,
  LucroPresumido = 8,
  TributacaoNormal = 9,
  SimplesNacionalComExcessoDoSublimite = 10,
  EmpresaDeResponsabilidadeLimitada = 11,
}
export interface IPersonWithIncludesParams {
  id?: string | null;
  personId: string;
  includes?: number[] | null;
}

export enum EPersonIncludeType {
  Access,
  Addresses,
  TargetComments,
  Integration,
  Phones,
  Preference,
  Responsible,
  Tax,
  EventConsolidated,
  FinancialConsolidated,
  HealthConsolidated,
  HistoryConsolidated,
  PhoneConsolidated,
  SubscriptionConsolidated,
  TechInfoConsolidated,
  UserConsolidated,
  StorageConsolidated,
}

export enum ETimelineEvent {
  SaleCreated,
  SaleDeleted,
  CustomerCreated,
  CustomerCreatedHotSite,
  VisitorCreated,
  VisitorConverted,
  VisitorAutoConverted,
  CustomerInactivated,
  CustomerAutoInactivated,
  CommentCreated,
  CommentContentEdited,
  CommentTitleEdited,
  CommentEdited,
  CommentDeleted,
  SubscriptionCreated,
  SubscriptionRenewed,
  SubscriptionAutoRenewed,
  SubscriptionBreak,
  SubscriptionDeleted,
  SubscriptionEnded,
  AccountsReceivableReceived,
  ReceiptsReversed,
  WorkoutPlanCreated,
  PhysicalAssessmentCreated,
  ServiceCreated,
  ServiceDeleted,
  ActivityConsumed,
  GoodConsumed,
  ServiceConsumed,
  ModuleConsumed,
  ActivityReturned,
  GoodReturned,
  ServiceReturned,
  ModuleReturned,
}

export type TTimelineEvent = {
  CategoryId: ETimelineEvent;
  Category: string;
  Title: string;
  Description: string;
  Extra: string;
  EventId: string;
  EventBy: string;
  EventByName: string;
  EventOn: string;
  PartsTitle?: (string | number)[]; // only front
  PartsDescription?: (string | number)[]; // only front
};

export const timelineIcons: Partial<Record<ETimelineEvent, string>> = {
  [ETimelineEvent.SaleCreated]: 'box-open',
  [ETimelineEvent.SaleDeleted]: 'cross-circle',
  [ETimelineEvent.CustomerCreated]: 'review',
  [ETimelineEvent.CustomerCreatedHotSite]: 'review',
  [ETimelineEvent.VisitorCreated]: 'user',
  [ETimelineEvent.VisitorConverted]: 'review',
  [ETimelineEvent.VisitorAutoConverted]: 'review',
  [ETimelineEvent.CustomerInactivated]: 'cross-circle',
  [ETimelineEvent.CustomerAutoInactivated]: 'cross-circle',
  [ETimelineEvent.CommentCreated]: 'note-sticky',
  [ETimelineEvent.CommentContentEdited]: 'pen-square',
  [ETimelineEvent.CommentTitleEdited]: 'pen-square',
  [ETimelineEvent.CommentEdited]: 'pen-square',
  [ETimelineEvent.CommentDeleted]: 'cross-circle',
  [ETimelineEvent.SubscriptionCreated]: 'memo-circle-check',
  [ETimelineEvent.SubscriptionRenewed]: 'memo-circle-check',
  [ETimelineEvent.SubscriptionAutoRenewed]: 'memo-circle-check',
  [ETimelineEvent.SubscriptionBreak]: 'cross-circle',
  [ETimelineEvent.SubscriptionDeleted]: 'cross-circle',
  [ETimelineEvent.SubscriptionEnded]: 'cross-circle',
  [ETimelineEvent.AccountsReceivableReceived]: 'bank',
  [ETimelineEvent.ReceiptsReversed]: 'refund-alt',
  [ETimelineEvent.ServiceCreated]: 'heart-partner-handshake',
  [ETimelineEvent.ServiceDeleted]: 'cross-circle',
  [ETimelineEvent.ActivityConsumed]: 'bolt',
  [ETimelineEvent.GoodConsumed]: 'bolt',
  [ETimelineEvent.ServiceConsumed]: 'bolt',
  [ETimelineEvent.ModuleConsumed]: 'bolt',
  [ETimelineEvent.ActivityReturned]: 'cross-circle',
  [ETimelineEvent.GoodReturned]: 'cross-circle',
  [ETimelineEvent.ServiceReturned]: 'cross-circle',
  [ETimelineEvent.ModuleReturned]: 'cross-circle',
};

export const timelineSvgs: Partial<Record<ETimelineEvent, string>> = {
  [ETimelineEvent.WorkoutPlanCreated]: 'treino-letter',
  [ETimelineEvent.PhysicalAssessmentCreated]: 'afig-small',
};

export const templateByParts: Record<ETimelineEvent, string[]> = {
  [ETimelineEvent.SaleCreated]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.SaleDeleted]: ['date', 'addedBy'],
  [ETimelineEvent.CustomerCreated]: ['date', 'addedBy'],
  [ETimelineEvent.CustomerCreatedHotSite]: ['date', 'addedBy'],
  [ETimelineEvent.VisitorCreated]: ['date', 'addedBy'],
  [ETimelineEvent.VisitorConverted]: ['date', 'addedBy'],
  [ETimelineEvent.VisitorAutoConverted]: ['date', 'addedBy'],
  [ETimelineEvent.CustomerInactivated]: ['date', 'addedBy'],
  [ETimelineEvent.CustomerAutoInactivated]: ['date', 'addedBy'],
  [ETimelineEvent.CommentCreated]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.CommentContentEdited]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.CommentTitleEdited]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.CommentEdited]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.CommentDeleted]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionCreated]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionRenewed]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionAutoRenewed]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionBreak]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionDeleted]: ['date', 'addedBy'],
  [ETimelineEvent.SubscriptionEnded]: ['date', 'addedBy'],
  [ETimelineEvent.AccountsReceivableReceived]: ['date', 'addedBy'],
  [ETimelineEvent.ReceiptsReversed]: ['date', 'addedBy'],
  [ETimelineEvent.WorkoutPlanCreated]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.PhysicalAssessmentCreated]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.ServiceCreated]: ['link', 'date', 'addedBy'],
  [ETimelineEvent.ServiceDeleted]: ['date', 'addedBy'],
  [ETimelineEvent.ActivityConsumed]: ['date', '', 'addedBy'],
  [ETimelineEvent.GoodConsumed]: ['date', '', 'addedBy'],
  [ETimelineEvent.ServiceConsumed]: ['date', '', 'addedBy'],
  [ETimelineEvent.ModuleConsumed]: ['date', '', 'addedBy'],
  [ETimelineEvent.ActivityReturned]: ['date', '', 'addedBy'],
  [ETimelineEvent.GoodReturned]: ['date', '', 'addedBy'],
  [ETimelineEvent.ServiceReturned]: ['date', '', 'addedBy'],
  [ETimelineEvent.ModuleReturned]: ['date', '', 'addedBy'],
};

export type TGraduation = {
  PersonActivityLevelId: string;
  ActivityId: string;
  PersonId: string;
  TeacherId: string;
  SubLevelId: string;
  GraduationOn: string;
  LevelId: string;
  PersonName?: string; //only front
};

export type TGraduationList = {
  PersonActivityLevelId: string;
  PersonId: string;
  ActivityId: string;
  ActivityName: string;
  TeacherId: string;
  TeacherName: string;
  LevelName: string;
  Color: string;
  SubLevelId: string;
  SublevelName: string;
  SubLevelOrder: number;
  GraduationOn: string;
  Level: {
    LevelId: string;
    Name: string;
    Color: string;
    ActivityId: string;
    SubLevelCount: number;
    SubLevels: {
      SubLevelId: string;
      Name: string;
      SubLevelOrder: number;
      LevelId: string;
    }[];
  };
};

export type TPersonIntegration = {
  GympassId: string;
  PersonId: string;
  TenantId: string;
  TotalpassId: string;
};

export type TPaguelaAccount = {
  ContaId: string;
  Nome: string;
  Sobrenome: string;
  TipoConta: string;
  Senha: string;
  TipoCadastro: string;
  Email: string;
  Telefone: string;
  CpfCnpj: string;
  RetornoUrl: string;
  RazaoSocial: string;
  Fantasia: string;
  InscricaoMunicipal: string;
  VendedorId: string;
  LogoUrl: string;
  Situacao: EPaguelaSituation;
  Cedentes: any[];
  Enderecos: {
    EnderecoId: string;
    ContaId: string;
    Logradouro: string;
    Numero: string;
    Complemento: string;
    Bairro: string;
    Cidade: string;
    Estado: string;
    Cep: string;
  }[];
  Cartoes: any[];
  Objetos: any[];
  Adquirentes: {
    AdquirenteId: string;
    Nome: string;
    ContaId: string;
    GatewayId: string;
    GatewayKey: string;
    UrlRequisicao: string;
    UrlConsulta: string;
    TipoMeioPagamento: string;
    MeioPagamento: string;
    Padrao: boolean;
    DataExclusao: string;
    DataCadastro: string;
    DataModificacao: string;
    Conta: any;
  }[];
  InformacoesBancarias: {
    InformacaoBancariaId: string;
    TipoConta: string;
    Agencia: string;
    AgenciaDigito: string;
    Banco: string;
    NumeroConta: string;
    ContaDigito: string;
    ContaBancariaZoop: any;
  }[];
  Pendencias: any[];
};
